import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { CardProps, TypographyProps } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';
import type { ComponentType, ElementType, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CardEditDialog } from './CardEditDialog';
import { CardIcon } from './CardIcon';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * The one card every section of Project details uses (brief §3, "Card
 * pattern"): an icon and a title — the header of Web's Storybook
 * `UI/EntityCard` (icon in a fog-20 circle, the name at body size; Mette,
 * 3 Nov) — ✎ top right (tooltip "Edit"), then the
 * card's content. Built on the theme's MUI `Card` — radius 12, 1px fog-20
 * border, 24px padding — so it matches the library's card without a style of
 * its own.
 *
 * Edit mode is a modal the card grows into (decision 16, `CardEditDialog`),
 * so the page never reflows while a card is edited: the form, then a footer
 * with the keyboard hint, Cancel and Save. After a save, "✓ Saved" stands in
 * the card's header for two seconds. A click anywhere on the card opens it;
 * ✎ is the keyboard's way in. A card with no form stays in view mode.
 *
 * The surface is Web's `EntityCard` on hover (decision 17): white at rest;
 * hovered or holding focus, a blue-80 border and a light-blue wash.
 */
export type DetailsCardProps = {
  /** Material Symbols glyph name. */
  icon: string;
  title: string;
  automationId: string;
  /** The view — what is true now. */
  children: ReactNode;
  /** The card's form; without one the card has no edit mode. */
  form?: ReactNode;
  editing?: boolean;
  /** "✓ Saved" in the header. */
  saved?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  /** A click outside the modal: closes it, or asks first with unsaved changes. */
  onRequestClose?: () => void;
  onSave?: () => void;
  /** The Save button's words, e.g. "Save · 200 can log time" (slice 4). */
  saveLabel?: string;
};

/** What a card passes on to `DetailsCard` for its edit mode. */
export type CardEditProps = Pick<
  DetailsCardProps,
  'form' | 'editing' | 'saved' | 'onEdit' | 'onCancel' | 'onRequestClose' | 'onSave' | 'saveLabel'
>;

/** "⌘+Enter" on a Mac, "Ctrl+Enter" elsewhere — the hint names the real keys. */
const saveKeys = (): string =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? '⌘+Enter'
    : 'Ctrl+Enter';

// Cast as Web does: Motion's wrapper types drop MUI's `component` prop.
const MotionCard = motion.create(Card as ComponentType<CardProps & { component?: ElementType }>);
const MotionTypography = motion.create(Typography as ComponentType<TypographyProps>);

/**
 * Web's `card-gradient-small` hover wash (`Web/src/assets/styles/_mixins.scss`):
 * the same conic gradient, faded in by opacity on a layer under the content
 * rather than by animating registered custom properties.
 */
const SURFACE_SX = {
  position: 'relative',
  isolation: 'isolate',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    borderRadius: 'inherit',
    background:
      'conic-gradient(from 20deg at 40% -18%, var(--color-black-10) 206deg, var(--color-blue-10) 230deg)',
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
  },
  '&:hover, &:focus-within': { borderColor: 'var(--color-blue-80)' },
  '&:hover::before, &:focus-within::before': { opacity: 1 },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&::before': { transition: 'none' },
  },
} as const;

/**
 * A click on the card opens it — but not one on a link or button inside it
 * (they act for themselves), one that ends a text selection, or one bubbling
 * up through React from the modal's portal.
 */
const opensCard = (event: MouseEvent<HTMLElement>): boolean => {
  const target = event.target as HTMLElement;
  if (!event.currentTarget.contains(target)) return false;
  if (target.closest('a, button')) return false;
  return !window.getSelection()?.toString();
};

export const DetailsCard = ({
  icon,
  title,
  automationId,
  children,
  form,
  editing = false,
  saved = false,
  onEdit,
  onCancel,
  onRequestClose,
  onSave,
  saveLabel,
}: DetailsCardProps) => {
  const { t } = useTranslation('pmDetails');
  const headingId = `${automationId}-title`;
  const editable = form !== undefined && onEdit !== undefined;
  const inEdit = editable && editing;
  const footer = (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 1,
        pt: 2,
        borderTop: 'var(--border-width-thin) solid var(--color-fog-20)',
      }}
    >
      <Typography variant="label" component="span" sx={{ flex: 1, color: 'var(--color-fog-80)' }}>
        {t('card.KeyHint', { keys: saveKeys() })}
      </Typography>
      <Button
        variant="text"
        color="secondary"
        onClick={onCancel}
        data-automation-id={`${automationId}-cancel`}
      >
        {t('card.Cancel')}
      </Button>
      <Button
        variant="contained"
        color="secondary"
        onClick={onSave}
        data-automation-id={`${automationId}-save`}
      >
        {saveLabel ?? t('card.Save')}
      </Button>
    </Stack>
  );
  return (
    <>
      <MotionCard
        // Shared with the modal's Paper: Motion grows the card into it.
        layoutId={automationId}
        component="section"
        id={automationId}
        aria-labelledby={headingId}
        data-automation-id={automationId}
        data-mode={inEdit ? 'edit' : 'view'}
        onClick={
          editable
            ? (event) => {
                if (opensCard(event)) onEdit?.();
              }
            : undefined
        }
        // Fills its grid cell, so the two cards in a row share a height.
        sx={{ height: '100%', ...(editable ? SURFACE_SX : {}) }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minHeight: 32 }}>
            <CardIcon name={icon} />
            <MotionTypography
              layoutId={`${automationId}-title`}
              id={headingId}
              variant="bodyBold"
              component="h2"
              sx={{ flex: 1, minWidth: 0 }}
            >
              {title}
            </MotionTypography>
            {saved ? (
              <Stack
                direction="row"
                role="status"
                sx={{ alignItems: 'center', gap: 0.5, color: 'var(--color-success-100)' }}
                data-automation-id={`${automationId}-saved`}
              >
                <MaterialSymbol name="check" size={18} />
                <Typography variant="bodyRegular" component="span" sx={{ color: 'inherit' }}>
                  {t('card.Saved')}
                </Typography>
              </Stack>
            ) : (
              <Tooltip title={t('card.Edit')}>
                <IconButton
                  size="small"
                  aria-label={`${t('card.Edit')}: ${title}`}
                  onClick={editable ? onEdit : undefined}
                  data-automation-id={`${automationId}-edit`}
                >
                  <MaterialSymbol name="edit" size={20} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Box data-automation-id={`${automationId}-view`}>{children}</Box>
        </CardContent>
      </MotionCard>
      <AnimatePresence>
        {inEdit ? (
          <CardEditDialog
            key={automationId}
            layoutId={automationId}
            icon={icon}
            title={title}
            footer={footer}
            onRequestClose={onRequestClose ?? onCancel ?? (() => undefined)}
          >
            {form}
          </CardEditDialog>
        ) : null}
      </AnimatePresence>
    </>
  );
};

/**
 * A card's label → value row: the label as a 12px muted line, the value
 * beside it. One grid per card, so the labels share a column.
 */
export const FactGrid = ({
  children,
  automationId,
}: {
  children: ReactNode;
  automationId?: string;
}) => (
  <Stack
    data-automation-id={automationId}
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(96px, auto) minmax(0, 1fr)',
      columnGap: 2,
      rowGap: 1.5,
      alignItems: 'center',
    }}
  >
    {children}
  </Stack>
);

export const FactLabel = ({ children }: { children: ReactNode }) => (
  <Typography variant="label" component="span" sx={{ color: 'var(--color-fog-80)' }}>
    {children}
  </Typography>
);

/** A note after a value — "· default", "· set at creation" — muted, 12px. */
export const ValueNote = ({
  children,
  automationId,
}: {
  children: ReactNode;
  automationId?: string;
}) => (
  <Typography
    variant="label"
    component="span"
    data-automation-id={automationId}
    sx={{ color: 'var(--color-fog-80)' }}
  >
    {children}
  </Typography>
);
